/**
 * Persistent powder residue — a cheap ground-plane accumulation buffer.
 *
 * Soft splats accumulate in a fixed RGBA8 field. The renderer uploads this
 * once per stamp (never per frame), mips it, and samples it as an albedo tint.
 * Reset wipes the field.
 */

export const POWDER_TEX_SIZE = 256;
export const POWDER_WORLD = {
  x0: -8,
  z0: -4,
  width: 16,
  depth: 14,
} as const;

export interface PowderColor {
  r: number;
  g: number;
  b: number;
}

export interface PowderStamp {
  x: number;
  z: number;
  /** World-space splat radius (m). */
  radius: number;
  color: PowderColor;
  /** Peak alpha added at the splat center, 0..1. */
  amount: number;
}

export interface PowderResidueField {
  readonly size: number;
  readonly data: Uint8ClampedArray;
  generation: number;
  dirty: boolean;
}

export function createPowderResidueField(size: number = POWDER_TEX_SIZE): PowderResidueField {
  return {
    size,
    data: new Uint8ClampedArray(size * size * 4),
    generation: 0,
    dirty: false,
  };
}

/**
 * Ground overlay is a PlaneGeometry rotated X = -90°, which maps local +Y to
 * world −Z, so texture V must be inverted to keep world +Z toward V = 0.
 */
export function worldToPowderUv(x: number, z: number): { u: number; v: number } {
  return {
    u: (x - POWDER_WORLD.x0) / POWDER_WORLD.width,
    v: 1 - (z - POWDER_WORLD.z0) / POWDER_WORLD.depth,
  };
}

export function powderUvToWorld(u: number, v: number): { x: number; z: number } {
  return {
    x: POWDER_WORLD.x0 + u * POWDER_WORLD.width,
    z: POWDER_WORLD.z0 + (1 - v) * POWDER_WORLD.depth,
  };
}

/** Map pulverized volume (m³) to a readable ground splat radius. */
export function powderSplatRadius(volume: number): number {
  const v = Math.max(0, volume);
  return Math.min(3.6, 0.78 + Math.sqrt(v) * 5.2);
}

/** Peak alpha for one event — a bomb should stain, a tap should dust. */
export function powderSplatAmount(volume: number): number {
  const v = Math.max(0, volume);
  return Math.min(0.94, 0.42 + v * 6.8);
}

export function clearPowderResidue(field: PowderResidueField): void {
  field.data.fill(0);
  field.generation += 1;
  field.dirty = true;
}

/**
 * Additive soft splat. Color mixes toward the incoming tint by coverage so
 * clay + mortar layers read as two-tone dust, not a flat brown.
 */
export function stampPowderSplat(field: PowderResidueField, stamp: PowderStamp): void {
  const { u, v } = worldToPowderUv(stamp.x, stamp.z);
  if (u < -0.05 || u > 1.05 || v < -0.05 || v > 1.05) return;
  const size = field.size;
  const radiusUv = Math.max(
    1.5 / size,
    stamp.radius / Math.max(POWDER_WORLD.width, POWDER_WORLD.depth),
  );
  const cx = u * (size - 1);
  const cy = v * (size - 1);
  const radPx = radiusUv * size;
  const minX = Math.max(0, Math.floor(cx - radPx - 1));
  const maxX = Math.min(size - 1, Math.ceil(cx + radPx + 1));
  const minY = Math.max(0, Math.floor(cy - radPx - 1));
  const maxY = Math.min(size - 1, Math.ceil(cy + radPx + 1));
  const amount = Math.min(1, Math.max(0, stamp.amount));
  const data = field.data;
  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      const dx = (x - cx) / radPx;
      const dy = (y - cy) / radPx;
      const d2 = dx * dx + dy * dy;
      if (d2 > 1) continue;
      const falloff = Math.exp(-d2 * 2.8) * (1 - d2);
      const add = amount * falloff;
      if (add <= 1e-4) continue;
      const i = (y * size + x) * 4;
      const srcA = data[i + 3] / 255;
      const outA = Math.min(1, srcA + add * (1 - srcA * 0.35));
      const mix = outA > 1e-5 ? (add * 255) / Math.max(outA * 255, 1) : 0;
      const k = Math.min(1, mix);
      data[i] = data[i] + (stamp.color.r - data[i]) * k;
      data[i + 1] = data[i + 1] + (stamp.color.g - data[i + 1]) * k;
      data[i + 2] = data[i + 2] + (stamp.color.b - data[i + 2]) * k;
      data[i + 3] = Math.round(outA * 255);
    }
  }
  field.dirty = true;
}

/** Mean alpha in a world-space disk — used for wall-base tint strength. */
export function samplePowderAlpha(
  field: PowderResidueField,
  x: number,
  z: number,
  radius = 0.45,
): number {
  const { u, v } = worldToPowderUv(x, z);
  const size = field.size;
  const radUv = radius / Math.max(POWDER_WORLD.width, POWDER_WORLD.depth);
  const samples = 9;
  let sum = 0;
  let n = 0;
  for (let iy = -1; iy <= 1; iy += 1) {
    for (let ix = -1; ix <= 1; ix += 1) {
      const su = u + ix * radUv;
      const sv = v + iy * radUv;
      if (su < 0 || su > 1 || sv < 0 || sv > 1) continue;
      const px = Math.round(su * (size - 1));
      const py = Math.round(sv * (size - 1));
      sum += field.data[(py * size + px) * 4 + 3];
      n += 1;
    }
  }
  void samples;
  return n === 0 ? 0 : sum / (n * 255);
}
